import { useQuery } from "@tanstack/react-query";
import type { ProductSizeOption, Recipe, RecipeStand } from "../models/Recipe";
import {
	getProductSizesForRecipe,
	getRecipeStands,
	getRecipes,
} from "../repositories/RecipesRepository";

export const RECIPES_QUERY_KEYS = {
	all: ["recipes"] as const,
	list: () => ["recipes", "list"] as const,
	stands: (recipeId: number | null) => ["recipes", "stands", recipeId] as const,
	productSizes: ["recipes", "productSizes"] as const,
};

export const useRecipes = () => {
	const query = useQuery<Recipe[], Error>({
		queryKey: RECIPES_QUERY_KEYS.list(),
		queryFn: () => getRecipes(),
	});

	return {
		recipes: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error,
		refetch: query.refetch,
	};
};

export const useRecipeStands = (recipeId: number | null) => {
	const query = useQuery<RecipeStand[], Error>({
		queryKey: RECIPES_QUERY_KEYS.stands(recipeId),
		queryFn: () => getRecipeStands(recipeId!),
		enabled: !!recipeId,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	return {
		stands: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error,
		refetch: query.refetch,
	};
};

export const useProductSizesForRecipe = (includeInactive = false) => {
	return useQuery<ProductSizeOption[], Error>({
		queryKey: [RECIPES_QUERY_KEYS.productSizes, includeInactive],
		queryFn: () => getProductSizesForRecipe(includeInactive),
		staleTime: 5 * 60 * 1000,
	});
};
